import { useCallback, useState, useMemo } from "react";
import { AssetType, SpacePermission, ItemType } from "@thenounproject/lingo-core";

import { Inspectable } from "@constants/Inspector";
import useGenerateFilecut from "@actions/useGenerateFilecut";
import useDownloadMultipleAssets from "@actions/useDownloadMultipleAssets";
import useDownloadFile from "@actions/useDownloadFile";
import useNotifications from "@actions/useNotifications";
import useNavPoint from "@hooks/useNavPoint";
import { useSelectSpace } from "@selectors/entities/spaces";
import { useDownloadErrorHandler } from "./useDownloadErrorHandler";
import { IconStatus } from "../types";

export type DownloadOptions = {
  /** File type for conversion (e.g., "PNG", "SVG"). If not provided, downloads original. */
  fileType?: string;
  /** Scale dimensions (e.g., "1x", "2x"). Defaults to "1x". */
  dimensions?: string;
  /** DPI for raster formats */
  dpi?: number;
};

export type DownloadState = {
  status: IconStatus;
  fileType?: string;
};

/**
 * Unified hook for downloading assets - supports both single and multi-asset downloads.
 *
 * Single downloads support conversion options (fileType, dimensions, dpi).
 * Multi-asset downloads currently only support original files.
 *
 * Future: Multi-asset conversions will be supported when backend is ready.
 */
export function useAssetDownload() {
  const [downloadState, setDownloadState] = useState<DownloadState>({ status: null });
  const [generateFilecut] = useGenerateFilecut();
  const [downloadMultipleAssets] = useDownloadMultipleAssets();
  const downloadFile = useDownloadFile();
  const { showNotification } = useNotifications();
  const { handleDownloadError } = useDownloadErrorHandler();
  const { portalId } = useNavPoint();
  const space = useSelectSpace();

  const isAdmin = useMemo(
    () => space?.access?.permissions?.includes(SpacePermission.viewAssetLibrary) === true,
    [space?.access?.permissions]
  );

  /**
   * Filter inspectables to only those that are downloadable (excludes colors and URLs)
   */
  const filterDownloadable = useCallback((inspectables: Inspectable[]): Inspectable[] => {
    return inspectables.filter(i => {
      const isAsset = !i.item || i.item.type === ItemType.asset;
      return Boolean(isAsset && i?.asset?.permalink && i?.asset?.type !== AssetType.URL);
    });
  }, []);

  /**
   * Download a single asset with optional conversion
   */
  const downloadSingle = useCallback(
    async (inspectable: Inspectable, options?: DownloadOptions) => {
      const { asset } = inspectable;
      if (!asset) return { success: false };

      const fileType = options?.fileType || asset.type,
        dimensions = options?.dimensions || "1x",
        dpi = options?.dpi;

      const assetName = asset.name || asset.meta?.font?.displayName || "Unnamed asset";

      showNotification({
        message: `Preparing ${assetName} ${fileType}${dimensions !== "1x" ? ` @ ${dimensions}` : ""} for download...`,
      });

      setDownloadState({ status: "loading", fileType });

      const action = await generateFilecut({
        inspectable,
        fileType,
        dimensions,
        dpi,
        portalId,
        isAdmin,
        userAction: "download",
      });

      if (action.error) {
        setDownloadState({ status: "error", fileType });

        const handled = handleDownloadError(action.error, {
          onRetry: () => downloadSingle(inspectable, options),
        });

        if (!handled) {
          showNotification({
            message:
              action.error.message ||
              "There was a problem preparing your file for download. Please try again.",
            level: "error",
            link: asset.permalink ? { text: "View asset.", url: asset.permalink } : undefined,
          });
        }

        setTimeout(() => setDownloadState({ status: null }), 3000);
        return { success: false, error: action.error };
      }

      showNotification({ message: "Your download should begin in just a second" });
      downloadFile(action.response.result.url);
      setDownloadState({ status: "success", fileType });

      setTimeout(() => setDownloadState({ status: null }), 3000);
      return { success: true, url: action.response.result.url };
    },
    [generateFilecut, portalId, isAdmin, handleDownloadError, showNotification, downloadFile]
  );

  /**
   * Download multiple assets as a zip (original files only)
   *
   * Future: Will support conversion options when backend supports batch conversions.
   */
  const downloadMultiple = useCallback(
    async (inspectables: Inspectable[]) => {
      const downloadable = filterDownloadable(inspectables);
      if (downloadable.length === 0) return { success: false };

      showNotification({
        message: `Preparing to download ${downloadable.length} assets.`,
      });

      setDownloadState({ status: "loading" });

      const action = await downloadMultipleAssets({
        inspectables: downloadable,
        isAdmin,
        portalId,
      });

      if (action.error) {
        setDownloadState({ status: "error" });

        const handled = handleDownloadError(action.error, {
          onRetry: () => downloadMultiple(inspectables),
        });

        if (!handled) {
          showNotification({
            level: "error",
            message:
              action.error.message ||
              "Unfortunately, there was a problem starting your download. Please try again later.",
          });
        }

        setTimeout(() => setDownloadState({ status: null }), 3000);
        return { success: false, error: action.error };
      }

      setDownloadState({ status: "success" });
      setTimeout(() => setDownloadState({ status: null }), 3000);
      return { success: true };
    },
    [
      filterDownloadable,
      downloadMultipleAssets,
      isAdmin,
      portalId,
      handleDownloadError,
      showNotification,
    ]
  );

  /**
   * Smart download - routes to appropriate method based on inspectables and options.
   *
   * - Single asset: uses downloadSingle (supports conversion options)
   * - Multiple assets: uses downloadMultiple (original files as zip)
   * - If conversion options provided with multiple assets, downloads first asset with conversion
   */
  const download = useCallback(
    async (inspectables: Inspectable[], options?: DownloadOptions) => {
      if (inspectables.length === 0) return { success: false };

      // If conversion options specified, download single asset
      if (options?.fileType || inspectables.length === 1) {
        return downloadSingle(inspectables[0], options);
      }

      // Multiple assets without conversion - download as zip
      return downloadMultiple(inspectables);
    },
    [downloadSingle, downloadMultiple]
  );

  return useMemo(
    () => ({
      /** Smart download - routes based on count and options */
      download,
      /** Download single asset with optional conversion */
      downloadSingle,
      /** Download multiple assets as zip (originals only) */
      downloadMultiple,
      /** Filter inspectables to only downloadable assets */
      filterDownloadable,
      /** Current download state */
      downloadState,
      /** Whether a download is in progress */
      isDownloading: downloadState.status === "loading",
    }),
    [download, downloadSingle, downloadMultiple, filterDownloadable, downloadState]
  );
}
